import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  Alert,
  Modal,
  TextInput,
  Dimensions,
  Platform,
  Linking,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import Header from "../components/Header";
import {
  getTeacherSchoolOpportunities,
  signUpForOpportunity,
  updateTeacherSignup,
  removeTeacherSignup,
  getTeacherSignups,
  updateTeacherProfile,
  getTeacherProfile,
  TeacherProfileData,
  createOpportunityForSchool,
} from "../services/parseService";

const { width } = Dimensions.get("window");
const isTablet = width >= 768;

type TabType = "overview" | "opportunities" | "signups" | "profile";

// Modern color palette — matches SchoolAdminDashboard
const getThemedColors = (theme: "light" | "dark") => ({
  background: theme === "dark" ? "#0F0F12" : "#F8FAFC",
  backgroundSecondary: theme === "dark" ? "#18181B" : "#FFFFFF",
  card: theme === "dark" ? "#1C1C21" : "#FFFFFF",
  cardHover: theme === "dark" ? "#232329" : "#F8FAFC",

  text: theme === "dark" ? "#FAFAFA" : "#0F172A",
  textSecondary: theme === "dark" ? "#A1A1AA" : "#64748B",
  textMuted: theme === "dark" ? "#71717A" : "#94A3B8",

  primary: "#3B82F6",
  primaryLight:
    theme === "dark"
      ? "rgba(59, 130, 246, 0.15)"
      : "rgba(59, 130, 246, 0.08)",

  success: "#10B981",
  successLight:
    theme === "dark"
      ? "rgba(16, 185, 129, 0.15)"
      : "rgba(16, 185, 129, 0.08)",

  warning: "#F59E0B",
  warningLight:
    theme === "dark"
      ? "rgba(245, 158, 11, 0.15)"
      : "rgba(245, 158, 11, 0.08)",

  error: "#EF4444",
  errorLight:
    theme === "dark"
      ? "rgba(239, 68, 68, 0.15)"
      : "rgba(239, 68, 68, 0.08)",

  purple: "#8B5CF6",
  purpleLight:
    theme === "dark"
      ? "rgba(139, 92, 246, 0.15)"
      : "rgba(139, 92, 246, 0.08)",

  primaryGlow: theme === "dark" ? "rgba(59, 130, 246, 0.4)" : "rgba(59, 130, 246, 0.2)",

  border: theme === "dark" ? "#27272A" : "#E2E8F0",
  borderLight: theme === "dark" ? "#3F3F46" : "#F1F5F9",
  divider: theme === "dark" ? "#27272A" : "#F1F5F9",
  inputBg: theme === "dark" ? "#27272A" : "#F8FAFC",
  buttonBg: theme === "dark" ? "#27272A" : "#F1F5F9",
  buttonHover: theme === "dark" ? "#3F3F46" : "#E2E8F0",

  // Sidebar
  sidebarBg: theme === "dark" ? "#18181B" : "#FFFFFF",
  sidebarBorder: theme === "dark" ? "#27272A" : "#E2E8F0",

  overlay: theme === "dark" ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.5)",
  avatarBg: "#3B82F6",
});

interface TeacherOpportunityData {
  schoolOpportunity: any;
  opportunity: any;
  teacherSignups: any[];
  userHasSignedUp: boolean;
}


const TeacherPortal: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const colors = getThemedColors(theme);

  // State
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [opportunities, setOpportunities] = useState<TeacherOpportunityData[]>(
    []
  );
  const [mySignups, setMySignups] = useState<Array<{ id: string; get: (key: string) => any }>>([]);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [signupForm, setSignupForm] = useState({
    availability: "",
    roomNumber: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSignup, setEditingSignup] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Create opportunity state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    organization: "",
    type: "Program",
    location: "",
    website: "",
    eligibility: "",
    cost: "",
    startDate: "",
    endDate: "",
    applicationCloseDate: "",
  });
  const [isSchoolOnly, setIsSchoolOnly] = useState(true); // Default to school-only

  // Profile state
  const [profileData, setProfileData] = useState<
    TeacherProfileData & { email: string; username: string }
  >({
    email: "",
    username: "",
    displayName: "",
    department: "",
    roomNumber: "",
    alternativeContact: "",
    contactPreference: "email",
    bio: "",
    profilePictureUrl: "",
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState<TeacherProfileData>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const themeIconRotation = useRef(new Animated.Value(0)).current;

  const isTeacher = currentUser && currentUser.get("userType") === "teacher";
  const schoolPointer = currentUser?.get("school");
  const schoolId = schoolPointer?.id;
  const schoolName = schoolPointer?.get
    ? schoolPointer.get("name") || "Your School"
    : "Your School";
  const schoolSlug = schoolPointer?.get ? (schoolPointer.get("slug") || "") : "";
  const teacherEmail = currentUser?.get("email") || "";
  const teacherName = profileData.displayName || currentUser?.get("name") || teacherEmail.split("@")[0] || "Teacher";

  const handleLogout = () => {
    if (Platform.OS === "web") {
      if (window.confirm("Are you sure you want to sign out?")) {
        logout();
        navigation.navigate("Home", {});
      }
    } else {
      Alert.alert(
        "Sign Out",
        "Are you sure you want to sign out?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Sign Out",
            style: "destructive",
            onPress: async () => {
              await logout();
              navigation.navigate("Home", {});
            },
          },
        ]
      );
    }
  };

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, []);

  useEffect(() => {
    Animated.sequence([
      Animated.timing(themeIconRotation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(themeIconRotation, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }),
    ]).start();
  }, [theme]);

  useEffect(() => {
    if (isTeacher) {
      if (schoolId) {
        loadData();
        loadProfile();
      } else {
        setError(
          "No school assigned to your account. Please contact your administrator."
        );
        setIsLoading(false);
      }
    } else if (currentUser) {
      setIsLoading(false);
    }
  }, [isTeacher, schoolId, currentUser]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (!schoolId || !currentUser?.id) {
        setError("Unable to load data. Please try logging in again.");
        setIsLoading(false);
        return;
      }

      const [opps, signups] = await Promise.all([
        getTeacherSchoolOpportunities(schoolId),
        getTeacherSignups(currentUser.id),
      ]);

      setOpportunities(opps);
      setMySignups(signups);
    } catch (err: any) {
      console.error("Error loading data:", err);
      setError(err.message || "Failed to load opportunities");
    } finally {
      setIsLoading(false);
    }
  };

  const loadProfile = async () => {
    if (!currentUser?.id) return;
    try {
      const profile = await getTeacherProfile(currentUser.id);
      setProfileData(profile);
    } catch (err) {
      console.error("Error loading profile:", err);
    }
  };

  const handleOpenSignup = (opportunity: TeacherOpportunityData) => {
    setSelectedOpportunity(opportunity);
    const existingSignup = opportunity.teacherSignups.find(
      (signup) => signup.get("teacher")?.id === currentUser?.id
    );

    if (existingSignup) {
      setEditingSignup(existingSignup);
      setSignupForm({
        availability: existingSignup.get("availability") || "",
        roomNumber: existingSignup.get("roomNumber") || "",
        notes: existingSignup.get("notes") || "",
      });
    } else {
      setEditingSignup(null);
      setSignupForm({ availability: "", roomNumber: "", notes: "" });
    }
    setShowSignupModal(true);
  };

  const handleCloseModal = () => {
    setShowSignupModal(false);
    setSelectedOpportunity(null);
    setEditingSignup(null);
    setSignupForm({ availability: "", roomNumber: "", notes: "" });
  };

  const handleSubmitSignup = async () => {
    if (!currentUser?.id || !selectedOpportunity) return;

    try {
      setIsSubmitting(true);
      if (editingSignup) {
        await updateTeacherSignup(editingSignup.id, currentUser.id, signupForm);
        showAlert("Success", "Your signup has been updated");
      } else {
        await signUpForOpportunity(
          currentUser.id,
          selectedOpportunity.schoolOpportunity.id,
          signupForm
        );
        showAlert(
          "Success",
          "You have signed up to help with this opportunity!"
        );
      }
      handleCloseModal();
      loadData();
    } catch (error: any) {
      console.error("Error submitting signup:", error);
      showAlert("Error", error.message || "Failed to submit signup");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveSignup = async (signupId: string) => {
    const confirmRemove = () => {
      return new Promise<boolean>((resolve) => {
        if (Platform.OS === "web") {
          resolve(
            window.confirm(
              "Are you sure you want to remove your signup for this opportunity?"
            )
          );
        } else {
          Alert.alert(
            "Remove Signup",
            "Are you sure you want to remove your signup for this opportunity?",
            [
              { text: "Cancel", onPress: () => resolve(false), style: "cancel" },
              { text: "Remove", onPress: () => resolve(true), style: "destructive" },
            ]
          );
        }
      });
    };

    const confirmed = await confirmRemove();
    if (!confirmed || !currentUser?.id) return;

    try {
      await removeTeacherSignup(signupId, currentUser.id);
      showAlert("Success", "Signup removed");
      loadData();
    } catch (error) {
      console.error("Error removing signup:", error);
      showAlert("Error", "Failed to remove signup");
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUser?.id) return;

    try {
      setIsSavingProfile(true);
      await updateTeacherProfile(currentUser.id, editedProfile);
      await loadProfile();
      setIsEditingProfile(false);
      showAlert("Success", "Profile updated successfully");
    } catch (error: any) {
      console.error("Error saving profile:", error);
      showAlert("Error", error.message || "Failed to save profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === "web") {
      window.alert(`${title}: ${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  // Basic YYYY-MM-DD validation
  const isValidDate = (value: string): boolean => {
    if (!value) return true; // empty is fine (optional)
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(new Date(value).getTime());
  };

  const handleCreateOpportunity = async () => {
    if (!currentUser?.id || !schoolId) return;

    if (!createForm.title.trim() || !createForm.description.trim() || !createForm.organization.trim()) {
      showAlert("Error", "Please fill in title, description, and organization");
      return;
    }

    // Validate date fields
    const dateFields = [
      { value: createForm.startDate, label: "Start Date" },
      { value: createForm.endDate, label: "End Date" },
      { value: createForm.applicationCloseDate, label: "Application Deadline" },
    ];
    for (const { value, label } of dateFields) {
      if (value && !isValidDate(value)) {
        showAlert("Error", `${label} must be in YYYY-MM-DD format`);
        return;
      }
    }

    try {
      setIsCreating(true);
      await createOpportunityForSchool(
        schoolId,
        {
          title: createForm.title.trim(),
          description: createForm.description.trim(),
          organization: createForm.organization.trim(),
          type: createForm.type,
          location: createForm.location.trim(),
          website: createForm.website.trim() || undefined,
          eligibility: createForm.eligibility.trim() || undefined,
          cost: createForm.cost.trim() || undefined,
          startDate: createForm.startDate || undefined,
          endDate: createForm.endDate || undefined,
          applicationCloseDate: createForm.applicationCloseDate || undefined,
        },
        currentUser.id,
        isSchoolOnly
      );

      showAlert(
        "Success",
        isSchoolOnly
          ? "Opportunity created for your school!"
          : "Opportunity created and added to the wider BC STEM database!"
      );
      setShowCreateModal(false);
      setCreateForm({
        title: "",
        description: "",
        organization: "",
        type: "Program",
        location: "",
        website: "",
        eligibility: "",
        cost: "",
        startDate: "",
        endDate: "",
        applicationCloseDate: "",
      });
      setIsSchoolOnly(true);
      loadData();
    } catch (error: any) {
      console.error("Error creating opportunity:", error);
      showAlert("Error", error.message || "Failed to create opportunity");
    } finally {
      setIsCreating(false);
    }
  };

  const startEditingProfile = () => {
    setEditedProfile({
      displayName: profileData.displayName,
      department: profileData.department,
      roomNumber: profileData.roomNumber,
      alternativeContact: profileData.alternativeContact,
      contactPreference: profileData.contactPreference,
      bio: profileData.bio,
    });
    setIsEditingProfile(true);
  };

  const displayName =
    profileData.displayName || profileData.username || "Teacher";
  // Stats
  const stats = {
    totalOpportunities: opportunities.length,
    mySignups: mySignups.length,
    availableToSignUp: opportunities.filter((o) => !o.userHasSignedUp).length,
  };

  // Not logged in
  if (!currentUser) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="school-outline" size={32} color={colors.primary} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Teacher Portal
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            Sign in with your teacher account to access opportunities and manage
            your signups.
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={() => navigation.navigate("Login")}
          >
            <Ionicons name="log-in-outline" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Sign In</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Not a teacher
  if (!isTeacher) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.errorLight }]}>
            <Ionicons name="lock-closed-outline" size={32} color={colors.error} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Access Restricted
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            This portal is only available to teachers.{"\n"}
            Current account: {currentUser.get("email")}
          </Text>
          <TouchableOpacity
            style={[styles.outlineButton, { borderColor: colors.border }]}
            onPress={() => navigation.navigate("Login")}
          >
            <Ionicons name="swap-horizontal-outline" size={20} color={colors.text} />
            <Text style={[styles.outlineButtonText, { color: colors.text }]}>
              Switch Account
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centeredMessage}>
          <View style={[styles.iconCircle, { backgroundColor: colors.errorLight }]}>
            <Ionicons name="alert-circle-outline" size={32} color={colors.error} />
          </View>
          <Text style={[styles.centeredTitle, { color: colors.text }]}>
            Unable to Load
          </Text>
          <Text style={[styles.centeredSubtitle, { color: colors.textSecondary }]}>
            {error}
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={loadData}
          >
            <Ionicons name="refresh-outline" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Sidebar component
  const renderSidebar = () => (
    <Animated.View
      style={[
        styles.sidebar,
        sidebarCollapsed && styles.sidebarCollapsed,
        { backgroundColor: colors.sidebarBg, borderRightColor: colors.sidebarBorder, opacity: fadeAnim },
      ]}
    >
      {/* Sidebar Header */}
      <View style={[styles.sidebarHeader, { borderBottomColor: colors.border }]}>
        {!sidebarCollapsed && (
          <View style={styles.teacherBadge}>
            <View style={[styles.teacherAvatar, { backgroundColor: colors.purpleLight }]}>
              <Ionicons name="person" size={20} color={colors.purple} />
            </View>
            <View style={styles.teacherInfo}>
              <Text style={[styles.teacherName, { color: colors.text }]} numberOfLines={1}>
                {teacherName}
              </Text>
              <Text style={[styles.teacherLabel, { color: colors.textMuted }]}>
                Teacher Portal
              </Text>
            </View>
          </View>
        )}
        <TouchableOpacity
          onPress={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={[styles.collapseBtn, { backgroundColor: colors.buttonBg }]}
        >
          <Ionicons
            name={sidebarCollapsed ? "chevron-forward" : "chevron-back"}
            size={18}
            color={colors.textSecondary}
          />
        </TouchableOpacity>
      </View>

      {/* Navigation */}
      <View style={styles.sidebarNav}>
        {[
          { id: "overview" as TabType, icon: "grid-outline", label: "Overview" },
          { id: "opportunities" as TabType, icon: "folder-outline", label: "Opportunities" },
          { id: "signups" as TabType, icon: "checkmark-circle-outline", label: "My Signups" },
          { id: "profile" as TabType, icon: "person-outline", label: "Profile" },
        ].map((item) => {
          const isActive = activeTab === item.id;
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.navItem,
                isActive && { backgroundColor: colors.primaryLight },
                sidebarCollapsed && styles.navItemCollapsed,
              ]}
              onPress={() => setActiveTab(item.id)}
              activeOpacity={0.7}
            >
              <Ionicons
                name={item.icon as any}
                size={20}
                color={isActive ? colors.primary : colors.textSecondary}
              />
              {!sidebarCollapsed && (
                <Text
                  style={[
                    styles.navLabel,
                    { color: isActive ? colors.primary : colors.textSecondary },
                    isActive && styles.navLabelActive,
                  ]}
                >
                  {item.label}
                </Text>
              )}
              {isActive && !sidebarCollapsed && (
                <View style={[styles.navIndicator, { backgroundColor: colors.primary }]} />
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Student Portal Link */}
      {schoolSlug && (
        <View style={[styles.sidebarPortalSection, { borderTopColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.portalButton,
              { backgroundColor: colors.successLight, borderColor: colors.success + "30" },
              sidebarCollapsed && styles.portalButtonCollapsed,
            ]}
            onPress={() => Linking.openURL(`https://bcstem.ca/${schoolSlug}`)}
          >
            <Ionicons name="people" size={18} color={colors.success} />
            {!sidebarCollapsed && (
              <Text style={[styles.portalButtonText, { color: colors.success }]}>
                Visit Student Portal
              </Text>
            )}
          </TouchableOpacity>
          {!sidebarCollapsed && (
            <Text style={[styles.portalUrl, { color: colors.textMuted }]}>
              bcstem.ca/{schoolSlug}
            </Text>
          )}
        </View>
      )}

      {/* Sidebar Footer */}
      <View style={[styles.sidebarFooter, { borderTopColor: colors.border }]}>
        <TouchableOpacity
          onPress={toggleTheme}
          style={[styles.themeBtn, { backgroundColor: colors.buttonBg }]}
        >
          <Animated.View
            style={{
              transform: [
                {
                  rotate: themeIconRotation.interpolate({
                    inputRange: [0, 1],
                    outputRange: ["0deg", "180deg"],
                  }),
                },
              ],
            }}
          >
            <Ionicons
              name={theme === "dark" ? "sunny" : "moon"}
              size={18}
              color={colors.textSecondary}
            />
          </Animated.View>
        </TouchableOpacity>
        {!sidebarCollapsed && (
          <View style={styles.userInfo}>
            <Text style={[styles.userName, { color: colors.text }]} numberOfLines={1}>
              {teacherName}
            </Text>
            <Text style={[styles.userEmail, { color: colors.textMuted }]} numberOfLines={1}>
              {teacherEmail}
            </Text>
          </View>
        )}
        <TouchableOpacity onPress={handleLogout} style={[styles.logoutBtn, { backgroundColor: colors.errorLight }]}>
          <Ionicons name="log-out-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  // Tab content renderers
  const renderOverview = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <Animated.View style={[styles.contentContainer, { opacity: fadeAnim }]}>
        {/* Welcome Section */}
        <View style={styles.welcomeSection}>
          <View>
            {/* <Text style={[styles.greeting, { color: colors.textSecondary }]}>
              Welcome,
            </Text> */}
            <Text style={[styles.pageTitle, { color: colors.text }]}>
              {displayName}
            </Text>
            <Text style={[styles.welcomeSchool, { color: colors.textSecondary }]}>
              {schoolName}
            </Text>
          </View>
          {/* {schoolSlug && (
            <TouchableOpacity
              style={[styles.portalLink, { backgroundColor: colors.primaryLight, borderColor: colors.primary }]}
              onPress={() => navigation.navigate("StudentPortal", { schoolSlug })}
            >
              <Ionicons name="open-outline" size={16} color={colors.primary} />
              <Text style={[styles.portalLinkText, { color: colors.primary }]}>
                View Student Portal
              </Text>
            </TouchableOpacity>
          )} */}
        </View>

      {/* Stats Grid */}
      <View style={styles.statsGrid}>
        <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.statIconWrap, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="folder-open" size={22} color={colors.primary} />
          </View>
          <View style={styles.statContent}>
            <Text style={[styles.statValue, { color: colors.text }]}>
              {stats.totalOpportunities}
            </Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              Available
            </Text>
          </View>
        </View>

        <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.statIconWrap, { backgroundColor: colors.successLight }]}>
            <Ionicons name="checkmark-circle" size={22} color={colors.success} />
          </View>
          <View style={styles.statContent}>
            <Text style={[styles.statValue, { color: colors.text }]}>
              {stats.mySignups}
            </Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              My Signups
            </Text>
          </View>
        </View>

        <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.statIconWrap, { backgroundColor: colors.purpleLight }]}>
            <Ionicons name="hand-left" size={22} color={colors.purple} />
          </View>
          <View style={styles.statContent}>
            <Text style={[styles.statValue, { color: colors.text }]}>
              {stats.availableToSignUp}
            </Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              To Sign Up
            </Text>
          </View>
        </View>
      </View>

      {/* Recent Signups */}
      {mySignups.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Recent Signups
            </Text>
            {mySignups.length > 3 && (
              <TouchableOpacity onPress={() => setActiveTab("signups")}>
                <Text style={[styles.viewAllLink, { color: colors.primary }]}>View All</Text>
              </TouchableOpacity>
            )}
          </View>
          {mySignups.slice(0, 3).map((signup) => {
            const opportunity = signup.get("opportunity");
            return (
              <TouchableOpacity
                key={signup.id}
                style={[styles.recentCard, { backgroundColor: colors.card, borderColor: colors.border }]}
                onPress={() => setActiveTab("signups")}
                activeOpacity={0.7}
              >
                <View style={styles.recentContent}>
                  <Text
                    style={[styles.recentTitle, { color: colors.text }]}
                    numberOfLines={1}
                  >
                    {opportunity?.get("title") || "Unknown Opportunity"}
                  </Text>
                  <View style={styles.recentMeta}>
                    {signup.get("roomNumber") && (
                      <View style={styles.metaItem}>
                        <Ionicons name="location-outline" size={14} color={colors.textMuted} />
                        <Text style={[styles.metaText, { color: colors.textMuted }]}>
                          Room {signup.get("roomNumber")}
                        </Text>
                      </View>
                    )}
                    {signup.get("availability") && (
                      <View style={styles.metaItem}>
                        <Ionicons name="time-outline" size={14} color={colors.textMuted} />
                        <Text style={[styles.metaText, { color: colors.textMuted }]}>
                          {signup.get("availability")}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
                <View style={[styles.activeBadge, { backgroundColor: colors.successLight }]}>
                  <Ionicons name="checkmark-circle" size={12} color={colors.success} />
                  <Text style={[styles.activeBadgeText, { color: colors.success }]}>
                    Active
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
      </Animated.View>
    </ScrollView>
  );

  const renderOpportunities = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <Animated.View style={[styles.contentContainer, { opacity: fadeAnim }]}>
        <View style={styles.pageHeader}>
        <View style={styles.pageTitleSection}>
          <Text style={[styles.pageTitle, { color: colors.text }]}>
            Opportunities
          </Text>
          <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
            Sign up to mentor students or add your own
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.primaryButton, { backgroundColor: colors.primary, marginTop: 0 }]}
          onPress={() => setShowCreateModal(true)}
        >
          <Ionicons name="add" size={20} color="#FFFFFF" />
          <Text style={styles.primaryButtonText}>Add New</Text>
        </TouchableOpacity>
      </View>

      {opportunities.length === 0 ? (
        <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.emptyIconWrap, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="folder-open-outline" size={32} color={colors.primary} />
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text }]}>
            No opportunities yet
          </Text>
          <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
            No opportunities have been added to your school yet.{"\n"}You can add one yourself.
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={() => setShowCreateModal(true)}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Add Opportunity</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.opportunitiesList}>
          {opportunities.map((item) => (
            <TouchableOpacity
              key={item.schoolOpportunity.id}
              style={[styles.opportunityCard, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={() => handleOpenSignup(item)}
              activeOpacity={0.7}
            >
              <View style={styles.opportunityHeader}>
                <View style={styles.opportunityInfo}>
                  <Text
                    style={[styles.opportunityTitle, { color: colors.text }]}
                    numberOfLines={2}
                  >
                    {item.opportunity.get("title")}
                  </Text>
                  <Text style={[styles.opportunityOrg, { color: colors.textSecondary }]}>
                    {item.opportunity.get("organization")}
                  </Text>
                </View>
                {item.userHasSignedUp && (
                  <View style={[styles.signedUpBadge, { backgroundColor: colors.successLight }]}>
                    <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                    <Text style={[styles.signedUpText, { color: colors.success }]}>
                      Signed Up
                    </Text>
                  </View>
                )}
              </View>

              <Text
                style={[styles.opportunityDescription, { color: colors.textSecondary }]}
                numberOfLines={2}
              >
                {item.opportunity.get("description")}
              </Text>

              <View style={styles.opportunityFooter}>
                <View style={styles.teacherCountContainer}>
                  <View style={styles.teacherAvatars}>
                    {item.teacherSignups.slice(0, 3).map((signup, idx) => {
                      const teacher = signup.get?.("teacher");
                      const name = teacher?.get?.("displayName") || teacher?.get?.("username") || "";
                      const initial = name.length > 0 ? name.charAt(0).toUpperCase() : "?";
                      return (
                        <View
                          key={idx}
                          style={[
                            styles.miniAvatar,
                            { backgroundColor: colors.purple, marginLeft: idx > 0 ? -8 : 0 },
                          ]}
                        >
                          <Text style={styles.miniAvatarText}>{initial}</Text>
                        </View>
                      );
                    })}
                  </View>
                  <Text style={[styles.teacherCount, { color: colors.textSecondary }]}>
                    {item.teacherSignups.length}{" "}
                    {item.teacherSignups.length === 1 ? "teacher" : "teachers"}
                  </Text>
                </View>

                <TouchableOpacity
                  style={[
                    styles.signupBtn,
                    {
                      backgroundColor: item.userHasSignedUp ? colors.buttonBg : colors.primary,
                      borderColor: item.userHasSignedUp ? colors.border : colors.primary,
                      borderWidth: item.userHasSignedUp ? 1 : 0,
                    },
                  ]}
                  onPress={() => handleOpenSignup(item)}
                >
                  <Ionicons
                    name={item.userHasSignedUp ? "pencil-outline" : "hand-left-outline"}
                    size={16}
                    color={item.userHasSignedUp ? colors.text : "#FFFFFF"}
                  />
                  <Text
                    style={[
                      styles.signupBtnText,
                      { color: item.userHasSignedUp ? colors.text : "#FFFFFF" },
                    ]}
                  >
                    {item.userHasSignedUp ? "Edit" : "Sign Up"}
                  </Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}
      </Animated.View>
    </ScrollView>
  );

  const renderSignups = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <Animated.View style={[styles.contentContainer, { opacity: fadeAnim }]}>
        <View style={styles.pageHeader}>
        <View style={styles.pageTitleSection}>
          <Text style={[styles.pageTitle, { color: colors.text }]}>My Signups</Text>
          <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
            Opportunities you've committed to mentor students for
          </Text>
        </View>
      </View>

      {mySignups.length === 0 ? (
        <View style={[styles.emptyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.emptyIconWrap, { backgroundColor: colors.purpleLight }]}>
            <Ionicons name="clipboard-outline" size={32} color={colors.purple} />
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text }]}>
            No signups yet
          </Text>
          <Text style={[styles.emptyDesc, { color: colors.textSecondary }]}>
            Browse available opportunities and sign up to help students.
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            onPress={() => setActiveTab("opportunities")}
          >
            <Ionicons name="search" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Browse Opportunities</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.opportunitiesList}>
          {mySignups.map((signup) => {
            const opportunity = signup.get("opportunity");
            return (
              <View
                key={signup.id}
                style={[styles.signupCard, { backgroundColor: colors.card, borderColor: colors.border }]}
              >
                <View style={styles.signupHeader}>
                  <View style={styles.signupInfo}>
                    <Text style={[styles.signupTitle, { color: colors.text }]}>
                      {opportunity?.get("title") || "Unknown"}
                    </Text>
                    <Text style={[styles.signupOrg, { color: colors.textSecondary }]}>
                      {opportunity?.get("organization") || ""}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => handleRemoveSignup(signup.id)}
                    style={[styles.iconBtn, { backgroundColor: colors.errorLight }]}
                  >
                    <Ionicons name="trash-outline" size={18} color={colors.error} />
                  </TouchableOpacity>
                </View>

                <View style={[styles.signupDetails, { backgroundColor: colors.buttonBg, borderColor: colors.borderLight }]}>
                  <View style={styles.detailRow}>
                    <Ionicons name="location-outline" size={16} color={colors.textMuted} />
                    <Text style={[styles.detailLabel, { color: colors.textMuted }]}>Room:</Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {signup.get("roomNumber") || "Not specified"}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Ionicons name="time-outline" size={16} color={colors.textMuted} />
                    <Text style={[styles.detailLabel, { color: colors.textMuted }]}>Availability:</Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {signup.get("availability") || "Not specified"}
                    </Text>
                  </View>
                  {signup.get("notes") && (
                    <View style={styles.detailRow}>
                      <Ionicons name="document-text-outline" size={16} color={colors.textMuted} />
                      <Text style={[styles.detailLabel, { color: colors.textMuted }]}>Notes:</Text>
                      <Text style={[styles.detailValue, { color: colors.text }]} numberOfLines={2}>
                        {signup.get("notes")}
                      </Text>
                    </View>
                  )}
                </View>

                <TouchableOpacity
                  style={[styles.editSignupBtn, { borderColor: colors.border }]}
                  onPress={() => {
                    const oppData = opportunities.find(
                      (o) => o.schoolOpportunity.id === signup.get("schoolOpportunity")?.id
                    );
                    if (oppData) handleOpenSignup(oppData);
                  }}
                >
                  <Ionicons name="pencil-outline" size={16} color={colors.primary} />
                  <Text style={[styles.editSignupText, { color: colors.primary }]}>
                    Edit Details
                  </Text>
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      )}
      </Animated.View>
    </ScrollView>
  );

  const renderProfile = () => (
    <ScrollView style={styles.mainScroll} showsVerticalScrollIndicator={false}>
      <Animated.View style={[styles.contentContainer, { opacity: fadeAnim }]}>
        <View style={styles.pageHeader}>
          <View style={styles.pageTitleSection}>
            <Text style={[styles.pageTitle, { color: colors.text }]}>My Profile</Text>
            <Text style={[styles.pageSubtitle, { color: colors.textSecondary }]}>
              Manage your contact information and preferences
            </Text>
          </View>
        </View>

      <View style={[styles.profileCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Avatar Section */}
        <View style={styles.profileAvatarSection}>
          <View style={[styles.profileAvatarCircle, { backgroundColor: colors.primaryLight }]}>
            <Ionicons name="person" size={32} color={colors.primary} />
          </View>
          <View style={styles.profileAvatarInfo}>
            <Text style={[styles.profileName, { color: colors.text }]}>
              {displayName}
            </Text>
            <Text style={[styles.profileEmail, { color: colors.textSecondary }]}>
              {profileData.email}
            </Text>
            <View style={[styles.profileBadge, { backgroundColor: colors.primaryLight }]}>
              <Text style={[styles.profileBadgeText, { color: colors.primary }]}>
                Teacher at {schoolName}
              </Text>
            </View>
          </View>
        </View>

        {/* Profile completeness hint */}
        {!isEditingProfile && (() => {
          const fields = [profileData.displayName, profileData.department, profileData.roomNumber, profileData.alternativeContact, profileData.contactPreference, profileData.bio];
          const filled = fields.filter(Boolean).length;
          if (filled >= fields.length) return null;
          return (
            <Text style={[styles.profileCompleteness, { color: colors.textMuted }]}>
              {filled} of {fields.length} profile fields filled — complete your profile so students can reach you
            </Text>
          );
        })()}

        <View style={[styles.profileDivider, { backgroundColor: colors.borderLight }]} />

        {/* Profile Fields */}
        {isEditingProfile ? (
          <View style={styles.editForm}>
            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>
                Display Name
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  {
                    backgroundColor: colors.inputBg,
                    color: colors.text,
                    borderColor: colors.border,
                  },
                ]}
                value={editedProfile.displayName}
                onChangeText={(text) =>
                  setEditedProfile({ ...editedProfile, displayName: text })
                }
                placeholder="Your display name"
                placeholderTextColor={colors.textMuted}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>
                Department
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  {
                    backgroundColor: colors.inputBg,
                    color: colors.text,
                    borderColor: colors.border,
                  },
                ]}
                value={editedProfile.department}
                onChangeText={(text) =>
                  setEditedProfile({ ...editedProfile, department: text })
                }
                placeholder="e.g., Science, Math, English"
                placeholderTextColor={colors.textMuted}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>
                Room Number
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  {
                    backgroundColor: colors.inputBg,
                    color: colors.text,
                    borderColor: colors.border,
                  },
                ]}
                value={editedProfile.roomNumber}
                onChangeText={(text) =>
                  setEditedProfile({ ...editedProfile, roomNumber: text })
                }
                placeholder="e.g., Room 204"
                placeholderTextColor={colors.textMuted}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>
                Alternative Contact
              </Text>
              <TextInput
                style={[
                  styles.formInput,
                  {
                    backgroundColor: colors.inputBg,
                    color: colors.text,
                    borderColor: colors.border,
                  },
                ]}
                value={editedProfile.alternativeContact}
                onChangeText={(text) =>
                  setEditedProfile({ ...editedProfile, alternativeContact: text })
                }
                placeholder="Phone, Teams, etc."
                placeholderTextColor={colors.textMuted}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>
                Contact Preference
              </Text>
              <View style={styles.preferenceOptions}>
                {(["email", "phone", "teams", "other"] as const).map((pref) => (
                  <TouchableOpacity
                    key={pref}
                    style={[
                      styles.preferenceOption,
                      {
                        backgroundColor:
                          editedProfile.contactPreference === pref
                            ? colors.primary
                            : colors.inputBg,
                        borderColor:
                          editedProfile.contactPreference === pref
                            ? colors.primary
                            : colors.border,
                      },
                    ]}
                    onPress={() =>
                      setEditedProfile({ ...editedProfile, contactPreference: pref })
                    }
                  >
                    <Text
                      style={[
                        styles.preferenceText,
                        {
                          color:
                            editedProfile.contactPreference === pref
                              ? "#fff"
                              : colors.text,
                        },
                      ]}
                    >
                      {pref.charAt(0).toUpperCase() + pref.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.formLabel, { color: colors.text }]}>Bio</Text>
              <TextInput
                style={[
                  styles.formTextarea,
                  {
                    backgroundColor: colors.inputBg,
                    color: colors.text,
                    borderColor: colors.border,
                  },
                ]}
                value={editedProfile.bio}
                onChangeText={(text) =>
                  setEditedProfile({ ...editedProfile, bio: text })
                }
                placeholder="Tell students a bit about yourself..."
                placeholderTextColor={colors.textMuted}
                multiline
                numberOfLines={4}
              />
            </View>

            <View style={styles.formActions}>
              <TouchableOpacity
                style={[styles.cancelBtn, { borderColor: colors.border }]}
                onPress={() => setIsEditingProfile(false)}
              >
                <Text style={[styles.cancelBtnText, { color: colors.text }]}>
                  Cancel
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveBtn, { backgroundColor: colors.primary }]}
                onPress={handleSaveProfile}
                disabled={isSavingProfile}
              >
                {isSavingProfile ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveBtnText}>Save Changes</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={styles.profileFields}>
            <View style={styles.profileField}>
              <View style={styles.profileFieldIcon}>
                <Ionicons
                  name="briefcase-outline"
                  size={18}
                  color={colors.textMuted}
                />
              </View>
              <View style={styles.profileFieldContent}>
                <Text
                  style={[styles.profileFieldLabel, { color: colors.textMuted }]}
                >
                  Department
                </Text>
                <Text style={[styles.profileFieldValue, { color: colors.text }]}>
                  {profileData.department || "Not set"}
                </Text>
              </View>
            </View>

            <View style={styles.profileField}>
              <View style={styles.profileFieldIcon}>
                <Ionicons
                  name="location-outline"
                  size={18}
                  color={colors.textMuted}
                />
              </View>
              <View style={styles.profileFieldContent}>
                <Text
                  style={[styles.profileFieldLabel, { color: colors.textMuted }]}
                >
                  Room Number
                </Text>
                <Text style={[styles.profileFieldValue, { color: colors.text }]}>
                  {profileData.roomNumber || "Not set"}
                </Text>
              </View>
            </View>

            <View style={styles.profileField}>
              <View style={styles.profileFieldIcon}>
                <Ionicons
                  name="call-outline"
                  size={18}
                  color={colors.textMuted}
                />
              </View>
              <View style={styles.profileFieldContent}>
                <Text
                  style={[styles.profileFieldLabel, { color: colors.textMuted }]}
                >
                  Alternative Contact
                </Text>
                <Text style={[styles.profileFieldValue, { color: colors.text }]}>
                  {profileData.alternativeContact || "Not set"}
                </Text>
              </View>
            </View>

            <View style={styles.profileField}>
              <View style={styles.profileFieldIcon}>
                <Ionicons
                  name="chatbubble-outline"
                  size={18}
                  color={colors.textMuted}
                />
              </View>
              <View style={styles.profileFieldContent}>
                <Text
                  style={[styles.profileFieldLabel, { color: colors.textMuted }]}
                >
                  Contact Preference
                </Text>
                <Text style={[styles.profileFieldValue, { color: colors.text }]}>
                  {profileData.contactPreference
                    ? profileData.contactPreference.charAt(0).toUpperCase() +
                      profileData.contactPreference.slice(1)
                    : "Email"}
                </Text>
              </View>
            </View>

            {profileData.bio && (
              <View style={styles.profileField}>
                <View style={styles.profileFieldIcon}>
                  <Ionicons
                    name="document-text-outline"
                    size={18}
                    color={colors.textMuted}
                  />
                </View>
                <View style={styles.profileFieldContent}>
                  <Text
                    style={[styles.profileFieldLabel, { color: colors.textMuted }]}
                  >
                    Bio
                  </Text>
                  <Text style={[styles.profileFieldValue, { color: colors.text }]}>
                    {profileData.bio}
                  </Text>
                </View>
              </View>
            )}

            <TouchableOpacity
              style={[styles.editProfileBtn, { backgroundColor: colors.primary }]}
              onPress={startEditingProfile}
            >
              <Ionicons name="pencil-outline" size={18} color="#FFFFFF" />
              <Text style={styles.editProfileBtnText}>Edit Profile</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      </Animated.View>
    </ScrollView>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <Header />
      <View style={styles.dashboard}>
        {renderSidebar()}
        <View style={[styles.mainArea, { backgroundColor: colors.background }]}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
                Loading...
              </Text>
            </View>
          ) : (
            <>
              {activeTab === "overview" && renderOverview()}
              {activeTab === "opportunities" && renderOpportunities()}
              {activeTab === "signups" && renderSignups()}
              {activeTab === "profile" && renderProfile()}
            </>
          )}
        </View>
      </View>

      {/* Signup Modal */}
      <Modal
        visible={showSignupModal}
        animationType="fade"
        transparent={true}
        onRequestClose={handleCloseModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseModal}>
          <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
                <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.modalTitle, { color: colors.text }]}>
                    {editingSignup ? "Edit Signup" : "Sign Up to Help"}
                  </Text>
                  <TouchableOpacity onPress={handleCloseModal}>
                    <Ionicons name="close" size={24} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.modalBody}>
                  <Text style={[styles.modalOpportunityTitle, { color: colors.text }]}>
                    {selectedOpportunity?.opportunity.get("title")}
                  </Text>
                  <Text
                    style={[
                      styles.modalOpportunityOrg,
                      { color: colors.textSecondary },
                    ]}
                  >
                    {selectedOpportunity?.opportunity.get("organization")}
                  </Text>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Your Availability
                    </Text>
                    <TextInput
                      style={[
                        styles.modalInput,
                        {
                          backgroundColor: colors.inputBg,
                          color: colors.text,
                          borderColor: colors.border,
                        },
                      ]}
                      placeholder="e.g., Lunch, After School, Block B"
                      placeholderTextColor={colors.textMuted}
                      value={signupForm.availability}
                      onChangeText={(text) =>
                        setSignupForm({ ...signupForm, availability: text })
                      }
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Room Number
                    </Text>
                    <TextInput
                      style={[
                        styles.modalInput,
                        {
                          backgroundColor: colors.inputBg,
                          color: colors.text,
                          borderColor: colors.border,
                        },
                      ]}
                      placeholder="e.g., Room 204"
                      placeholderTextColor={colors.textMuted}
                      value={signupForm.roomNumber}
                      onChangeText={(text) =>
                        setSignupForm({ ...signupForm, roomNumber: text })
                      }
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Notes for Students
                    </Text>
                    <TextInput
                      style={[
                        styles.modalTextarea,
                        {
                          backgroundColor: colors.inputBg,
                          color: colors.text,
                          borderColor: colors.border,
                        },
                      ]}
                      placeholder="Any additional information for students..."
                      placeholderTextColor={colors.textMuted}
                      value={signupForm.notes}
                      onChangeText={(text) =>
                        setSignupForm({ ...signupForm, notes: text })
                      }
                      multiline
                      numberOfLines={4}
                    />
                  </View>
                </ScrollView>

                <View style={[styles.modalFooter, { borderTopColor: colors.border }]}>
                  <TouchableOpacity
                    style={[styles.modalCancelBtn, { borderColor: colors.border }]}
                    onPress={handleCloseModal}
                    disabled={isSubmitting}
                  >
                    <Text style={[styles.modalCancelText, { color: colors.text }]}>
                      Cancel
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalSubmitBtn, { backgroundColor: colors.primary }]}
                    onPress={handleSubmitSignup}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.modalSubmitText}>
                        {editingSignup ? "Update" : "Sign Up"}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Create Opportunity Modal */}
      <Modal
        visible={showCreateModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowCreateModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowCreateModal(false)}>
          <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View style={[styles.createModalContent, { backgroundColor: colors.card }]}>
                <View style={[styles.modalHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.modalTitle, { color: colors.text }]}>
                    Add New Opportunity
                  </Text>
                  <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                    <Ionicons name="close" size={24} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
                  {/* Visibility Toggle */}
                  <View style={[styles.visibilitySection, { backgroundColor: colors.inputBg, borderColor: colors.border }]}>
                    <Text style={[styles.visibilityTitle, { color: colors.text }]}>
                      Visibility
                    </Text>
                    <View style={styles.visibilityOptions}>
                      <TouchableOpacity
                        style={[
                          styles.visibilityOption,
                          {
                            backgroundColor: isSchoolOnly ? colors.primary : colors.card,
                            borderColor: isSchoolOnly ? colors.primary : colors.border,
                          },
                        ]}
                        onPress={() => setIsSchoolOnly(true)}
                      >
                        <Ionicons
                          name="school-outline"
                          size={18}
                          color={isSchoolOnly ? "#fff" : colors.textSecondary}
                        />
                        <Text
                          style={[
                            styles.visibilityOptionText,
                            { color: isSchoolOnly ? "#fff" : colors.text },
                          ]}
                        >
                          {schoolName} Only
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.visibilityOption,
                          {
                            backgroundColor: !isSchoolOnly ? colors.primary : colors.card,
                            borderColor: !isSchoolOnly ? colors.primary : colors.border,
                          },
                        ]}
                        onPress={() => setIsSchoolOnly(false)}
                      >
                        <Ionicons
                          name="globe-outline"
                          size={18}
                          color={!isSchoolOnly ? "#fff" : colors.textSecondary}
                        />
                        <Text
                          style={[
                            styles.visibilityOptionText,
                            { color: !isSchoolOnly ? "#fff" : colors.text },
                          ]}
                        >
                          BC STEM Database
                        </Text>
                      </TouchableOpacity>
                    </View>
                    <Text style={[styles.visibilityHint, { color: colors.textMuted }]}>
                      {isSchoolOnly
                        ? "Only visible to students and teachers at your school"
                        : "Visible to everyone on bcstem.ca + your school portal"}
                    </Text>
                  </View>

                  {/* Form Fields */}
                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Title <Text style={{ color: colors.error }}>*</Text>
                    </Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="e.g., Summer Science Research Program"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.title}
                      onChangeText={(text) => setCreateForm({ ...createForm, title: text })}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Organization <Text style={{ color: colors.error }}>*</Text>
                    </Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="e.g., UBC, SFU, or your school name"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.organization}
                      onChangeText={(text) => setCreateForm({ ...createForm, organization: text })}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      Description <Text style={{ color: colors.error }}>*</Text>
                    </Text>
                    <TextInput
                      style={[styles.modalTextarea, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="Describe the opportunity, what students will learn, requirements, etc."
                      placeholderTextColor={colors.textMuted}
                      value={createForm.description}
                      onChangeText={(text) => setCreateForm({ ...createForm, description: text })}
                      multiline
                      numberOfLines={4}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Type</Text>
                    <View style={styles.typeOptions}>
                      {["Program", "Competition", "Workshop", "Scholarship", "Internship", "Other"].map((type) => (
                        <TouchableOpacity
                          key={type}
                          style={[
                            styles.typeOption,
                            {
                              backgroundColor: createForm.type === type ? colors.primaryLight : colors.inputBg,
                              borderColor: createForm.type === type ? colors.primary : colors.border,
                            },
                          ]}
                          onPress={() => setCreateForm({ ...createForm, type })}
                        >
                          <Text
                            style={[
                              styles.typeOptionText,
                              { color: createForm.type === type ? colors.primary : colors.text },
                            ]}
                          >
                            {type}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Location</Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="e.g., Vancouver, BC or Online"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.location}
                      onChangeText={(text) => setCreateForm({ ...createForm, location: text })}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Website</Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="https://..."
                      placeholderTextColor={colors.textMuted}
                      value={createForm.website}
                      onChangeText={(text) => setCreateForm({ ...createForm, website: text })}
                      autoCapitalize="none"
                      keyboardType="url"
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Eligibility</Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="e.g., Grades 10-12, BC residents"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.eligibility}
                      onChangeText={(text) => setCreateForm({ ...createForm, eligibility: text })}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Cost</Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="e.g., Free, $50, etc."
                      placeholderTextColor={colors.textMuted}
                      value={createForm.cost}
                      onChangeText={(text) => setCreateForm({ ...createForm, cost: text })}
                    />
                  </View>

                  <View style={styles.modalFormGroup}>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>Application Deadline</Text>
                    <TextInput
                      style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={colors.textMuted}
                      value={createForm.applicationCloseDate}
                      onChangeText={(text) => setCreateForm({ ...createForm, applicationCloseDate: text })}
                    />
                  </View>

                  <View style={styles.formRow}>
                    <View style={[styles.modalFormGroup, { flex: 1 }]}>
                      <Text style={[styles.modalLabel, { color: colors.text }]}>Start Date</Text>
                      <TextInput
                        style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={colors.textMuted}
                        value={createForm.startDate}
                        onChangeText={(text) => setCreateForm({ ...createForm, startDate: text })}
                      />
                    </View>
                    <View style={[styles.modalFormGroup, { flex: 1 }]}>
                      <Text style={[styles.modalLabel, { color: colors.text }]}>End Date</Text>
                      <TextInput
                        style={[styles.modalInput, { backgroundColor: colors.inputBg, color: colors.text, borderColor: colors.border }]}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={colors.textMuted}
                        value={createForm.endDate}
                        onChangeText={(text) => setCreateForm({ ...createForm, endDate: text })}
                      />
                    </View>
                  </View>
                </ScrollView>

                <View style={[styles.modalFooter, { borderTopColor: colors.border }]}>
                  <TouchableOpacity
                    style={[styles.modalCancelBtn, { borderColor: colors.border }]}
                    onPress={() => setShowCreateModal(false)}
                    disabled={isCreating}
                  >
                    <Text style={[styles.modalCancelText, { color: colors.text }]}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalSubmitBtn, { backgroundColor: colors.primary }]}
                    onPress={handleCreateOpportunity}
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.modalSubmitText}>Create</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dashboard: {
    flex: 1,
    flexDirection: "row",
  },

  // Sidebar
  sidebar: {
    width: 260,
    borderRightWidth: 1,
    flexDirection: "column",
  },
  sidebarCollapsed: {
    width: 72,
  },
  sidebarHeader: {
    padding: 20,
    borderBottomWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  teacherBadge: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 12,
  },
  teacherAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  teacherInfo: {
    marginLeft: 12,
    flex: 1,
  },
  teacherName: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  teacherLabel: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  collapseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarNav: {
    flex: 1,
    padding: 12,
  },
  navItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 4,
    position: "relative",
  },
  navItemCollapsed: {
    justifyContent: "center",
    paddingHorizontal: 0,
  },
  navLabel: {
    fontSize: 14,
    marginLeft: 12,
    fontFamily: "Inter-Medium",
    fontWeight: "500",
  },
  navLabelActive: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  navIndicator: {
    position: "absolute",
    right: 12,
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  sidebarPortalSection: {
    padding: 12,
    borderTopWidth: 1,
  },
  portalButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  portalButtonCollapsed: {
    paddingHorizontal: 0,
  },
  portalButtonText: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  portalUrl: {
    fontSize: 11,
    textAlign: "center",
    marginTop: 6,
    fontFamily: "Inter-Regular",
  },
  sidebarFooter: {
    padding: 12,
    borderTopWidth: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  userInfo: {
    marginLeft: 12,
    marginRight: 8,
    flex: 1,
  },
  userName: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  userEmail: {
    fontSize: 11,
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },

  // Main area
  mainArea: {
    flex: 1,
  },
  mainScroll: {
    flex: 1,
  },
  contentContainer: {
    padding: 24,
  },

  // Centered message (auth/error states)
  centeredMessage: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  centeredTitle: {
    fontSize: 22,
    fontWeight: "600",
    marginBottom: 12,
    textAlign: "center",
    fontFamily: "Inter-SemiBold",
  },
  centeredSubtitle: {
    fontSize: 15,
    textAlign: "center",
    lineHeight: 22,
    maxWidth: 400,
    marginBottom: 24,
    fontFamily: "Inter-Regular",
  },

  // Buttons
  primaryButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    marginTop: 16,
    gap: 8,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  outlineButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  outlineButtonText: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Portal Header
  portalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  headerSubtitle: {
    fontSize: 13,
    marginTop: 1,
    fontFamily: "Inter-Regular",
  },
  themeBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  logoutBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },

  // Tab Navigation
  tabNav: {
    flexDirection: "row",
    borderBottomWidth: 1,
    paddingHorizontal: 12,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    gap: 6,
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  tabLabelActive: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Content
  content: {
    flex: 1,
  },
  scrollContentContainer: {
    paddingBottom: 40,
  },
  tabContent: {
    padding: isTablet ? 32 : 20,
    maxWidth: 1000,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },

  // Welcome Section
  welcomeSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28,
  },
  greeting: {
    fontSize: 14,
    marginBottom: 4,
    fontFamily: "Inter-Regular",
  },
  welcomeSchool: {
    fontSize: 14,
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  portalLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  portalLinkText: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Page Headers
  pageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    gap: 12,
  },
  pageTitleSection: {
    flex: 1,
    minWidth: 200,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },
  pageSubtitle: {
    fontSize: 15,
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },

  // Stats Grid
  statsGrid: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    padding: 20,
    borderRadius: 14,
    borderWidth: 1,
    ...(Platform.OS === "web" && {
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    }),
  },
  statIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: 28,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },
  statLabel: {
    fontSize: 13,
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },

  // Section
  section: {
    marginBottom: 32,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  viewAllLink: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },

  // Recent Card
  recentCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 8,
  },
  recentContent: {
    flex: 1,
    marginRight: 12,
  },
  recentTitle: {
    fontSize: 15,
    fontWeight: "500",
    marginBottom: 4,
    fontFamily: "Inter-Medium",
  },
  recentMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    fontSize: 13,
    fontFamily: "Inter-Regular",
  },
  activeBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    gap: 4,
  },
  activeBadgeText: {
    fontSize: 12,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Empty State
  emptyCard: {
    padding: 40,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: "center",
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: "600",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  emptyDesc: {
    fontSize: 14,
    textAlign: "center",
    maxWidth: 300,
    lineHeight: 20,
    fontFamily: "Inter-Regular",
  },

  // Opportunities List
  opportunitiesList: {
    gap: 12,
  },
  opportunityCard: {
    padding: 20,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 12,
    ...(Platform.OS === "web" && {
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    }),
  },
  opportunityHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 12,
  },
  opportunityInfo: {
    flex: 1,
  },
  opportunityTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  opportunityOrg: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  signedUpBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    gap: 4,
  },
  signedUpText: {
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  opportunityDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
    fontFamily: "Inter-Regular",
  },
  opportunityFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  teacherCountContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  teacherAvatars: {
    flexDirection: "row",
  },
  miniAvatar: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
  },
  miniAvatarText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  teacherCount: {
    fontSize: 13,
    fontFamily: "Inter-Regular",
  },
  signupBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 6,
  },
  signupBtnText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Signup Card
  signupCard: {
    padding: 20,
    borderRadius: 14,
    borderWidth: 1,
  },
  signupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
  },
  signupInfo: {
    flex: 1,
    marginRight: 12,
  },
  signupTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  signupOrg: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  signupDetails: {
    padding: 14,
    borderRadius: 10,
    gap: 10,
    marginBottom: 14,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  detailLabel: {
    fontSize: 13,
    width: 80,
    fontFamily: "Inter-Regular",
  },
  detailValue: {
    flex: 1,
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  editSignupBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  editSignupText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Profile Card
  profileCard: {
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    padding: 20,
  },
  profileAvatarSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  },
  profileAvatarCircle: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  profileAvatarInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  profileEmail: {
    fontSize: 14,
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  profileBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  profileBadgeText: {
    fontSize: 12,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  profileCompleteness: {
    fontSize: 13,
    marginBottom: 16,
    lineHeight: 18,
    fontFamily: "Inter-Regular",
  },
  profileDivider: {
    height: 1,
    marginBottom: 20,
  },
  profileFields: {
    gap: 16,
  },
  profileField: {
    flexDirection: "row",
    gap: 14,
  },
  profileFieldIcon: {
    width: 36,
    alignItems: "center",
    paddingTop: 2,
  },
  profileFieldContent: {
    flex: 1,
  },
  profileFieldLabel: {
    fontSize: 12,
    marginBottom: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontFamily: "Inter-Medium",
  },
  profileFieldValue: {
    fontSize: 15,
    fontFamily: "Inter-Regular",
  },
  editProfileBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 10,
    marginTop: 8,
    gap: 8,
  },
  editProfileBtnText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Edit Form
  editForm: {
    gap: 18,
  },
  formGroup: {
    gap: 6,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    fontFamily: "Inter-Regular",
  },
  formTextarea: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    minHeight: 100,
    textAlignVertical: "top",
    fontFamily: "Inter-Regular",
  },
  preferenceOptions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  preferenceOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  preferenceText: {
    fontSize: 13,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  formActions: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
  },
  cancelBtnText: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  saveBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
  },
  saveBtnText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Modal
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    width: "100%",
    maxWidth: 500,
    maxHeight: "85%",
    borderRadius: 16,
    overflow: "hidden",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 20,
    borderBottomWidth: 1,
    gap: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    flex: 1,
    fontFamily: "Inter-SemiBold",
  },
  modalBody: {
    padding: 20,
  },
  modalOpportunityTitle: {
    fontSize: 17,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  modalOpportunityOrg: {
    fontSize: 14,
    marginBottom: 20,
    fontFamily: "Inter-Regular",
  },
  modalFormGroup: {
    marginBottom: 16,
  },
  modalLabel: {
    fontSize: 13,
    fontWeight: "500",
    marginBottom: 8,
    fontFamily: "Inter-Medium",
  },
  modalInput: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    fontFamily: "Inter-Regular",
  },
  modalTextarea: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    minHeight: 100,
    textAlignVertical: "top",
    fontFamily: "Inter-Regular",
  },
  modalFooter: {
    flexDirection: "row",
    padding: 20,
    borderTopWidth: 1,
    gap: 12,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
  },
  modalCancelText: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  modalSubmitBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
  },
  modalSubmitText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Create Modal
  createModalContent: {
    width: "100%",
    maxWidth: 640,
    maxHeight: "90%",
    borderRadius: 16,
    overflow: "hidden",
  },
  visibilitySection: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  visibilityTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 12,
    fontFamily: "Inter-SemiBold",
  },
  visibilityOptions: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  visibilityOption: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  visibilityOptionText: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  visibilityHint: {
    fontSize: 12,
    textAlign: "center",
    fontFamily: "Inter-Regular",
  },
  typeOptions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  typeOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  typeOptionText: {
    fontSize: 13,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  formRow: {
    flexDirection: "row",
    gap: 12,
  },
});

export default TeacherPortal;
